<script setup lang="ts">
import {
    computed, onMounted, ref, watch,
} from 'vue';

const props = defineProps<{
    pagination: {
        page: number,
        rowsPerPage: number,
    },
    numRows: number,
    paginationLabel?: string,
}>();

const localPagination = ref(props.pagination);

const paginationOptions = [
    { label: '15', value: 15 },
    { label: '30', value: 30 },
    { label: '50', value: 50 },
    { label: 'All', value: 0 },
];

const selectedOption = computed({
    get: () => paginationOptions.find((option) => option.value === localPagination.value.rowsPerPage),
    set: (option) => { localPagination.value.rowsPerPage = option?.value ?? localPagination.value.rowsPerPage; },
});

watch(
    () => props.pagination,
    (newPagination) => {
        localPagination.value = newPagination;
    },
    { deep: true },
);

const emits = defineEmits(['update:pagination']);

const max = computed(() => (
    props.pagination.rowsPerPage !== 0
        ? Math.ceil(props.numRows / props.pagination.rowsPerPage)
        : 1
));
const curStart = computed(() => (
    props.pagination.rowsPerPage !== 0
        ? (props.pagination.page - 1) * props.pagination.rowsPerPage + 1
        : 1
));
const curEnd = computed(() => (
    props.pagination.rowsPerPage !== 0
        ? Math.min(props.pagination.page * props.pagination.rowsPerPage, props.numRows)
        : props.numRows
));

const updatePagination = (newPagination: typeof props.pagination) => {
    emits('update:pagination', newPagination);
};

defineExpose({
    max,
    curStart,
    curEnd,
});

onMounted(() => {
    // A fix for ARC's empty label text error
    document.querySelector('.page-input')?.setAttribute('aria-label', 'Page number input');

    // Add a title to the pagination buttons in order to add more context to the labels
    document.querySelectorAll('.pagination-inputs .q-btn').forEach((el) => el.setAttribute('title', 'toggle page'));
});
</script>

<template>
    <div class="q-table__separator col text-fsum-all-blue bg-fsum-all-blue" />
    <div class="select-page-students q-table__control text-fsum-all-blue bg-fsum-all-blue">
        <span class="q-table__bottom-item">
            {{ paginationLabel ?? 'Rows per page' }}
        </span>
        <q-select
            v-model="selectedOption"
            :options="paginationOptions"
            dense
            options-dense
            borderless
            class="inline q-table__bottom-item missing-tab-outline"
            title="Select number of rows per page"
            aria-label="Select number of rows per page"
            role="region"
            tabindex="0"
            @update:model-value="updatePagination(localPagination)"
        />
    </div>
    <div class="q-table__control">
        <span class="q-table__bottom-item">
            {{ curStart }}-{{ curEnd }} of {{ numRows }}
        </span>
        <q-pagination
            v-model="localPagination.page"
            :max="max"
            color="fsum-all-blue"
            class="q-table__bottom-item pagination-inputs"
            input
            icon-first="keyboard_double_arrow_left"
            icon-last="keyboard_double_arrow_right"
            input-class="page-input"
            @update:model-value="updatePagination(localPagination)"
        />
    </div>
</template>
